import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal';
import Descriptions from 'antd/lib/descriptions';
import Select from 'antd/lib/select';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { selectAppSettings } from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';
import { IssueSourceType, SubscriptionFeatureType } from '@/types';
import { displayTextForIssueSourceType } from '@/helpers/displayHelpers';
import { useUpdateTriageDisabledSourcesMutation } from '@/middlewares/defaultApi';
import { updateAppSettingsSuccessAction } from '@/actions/appSettings';
import { globalMessage } from '@/utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import { useRestrictedFeatureIsDisabled } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { runwayColors } from '../styles/globalStyles';
import TicketFiltersSettings from '../triage/TicketFiltersSettings';

const selectableSourceTypes = Object.values(IssueSourceType).filter((type) => type !== IssueSourceType.Runway);

const sourceTypeOptions = selectableSourceTypes.map((type) => ({
    label: displayTextForIssueSourceType(type),
    value: type,
}));

const enabledSourcesFromDisabled = (disabledSources: IssueSourceType[] | undefined): IssueSourceType[] =>
    selectableSourceTypes.filter((type) => !(disabledSources ?? []).includes(type));

const TriageSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const dispatch = useDispatch();
    const isPaywalled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const disabledSources = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.triageSettings?.disabledSources,
    );
    const savedEnabledSources = enabledSourcesFromDisabled(disabledSources);

    const [sourcesModalOpen, setSourcesModalOpen] = React.useState(false);
    const [selectedSources, setSelectedSources] = React.useState<IssueSourceType[]>(savedEnabledSources);

    const [updateTriageDisabledSources, { isLoading, data: updateResponse }] = useUpdateTriageDisabledSourcesMutation();

    const openSourcesModal = () => {
        setSelectedSources(savedEnabledSources);
        setSourcesModalOpen(true);
    };

    const onSaveSources = () => {
        updateTriageDisabledSources({
            orgId,
            appId,
            disabledSources: selectableSourceTypes.filter((type) => !selectedSources.includes(type)),
        });
    };

    React.useEffect(() => {
        if (updateResponse?.success) {
            dispatch(updateAppSettingsSuccessAction({ appId, appSettings: updateResponse.data }));
            globalMessage.success({ content: 'Triage signals updated!', key: 'triageSignalsUpdated' });
            setSourcesModalOpen(false);
        } else if (updateResponse?.errorMessage) {
            globalMessage.error(updateResponse.errorMessage);
        }
    }, [updateResponse, appId, dispatch]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Descriptions size="middle" layout="horizontal" bordered column={1} labelStyle={{ width: 300 }}>
                <Descriptions.Item
                    label={
                        <PlanRestrictedFeatureWrapper
                            feature={SubscriptionFeatureType.Triage}
                            requiresTooltip
                            shouldFakeDisabledState
                        >
                            <Text style={{ color: runwayColors.darkTextGrey }}>Signals</Text>
                        </PlanRestrictedFeatureWrapper>
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        {savedEnabledSources.length > 0 ?
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {savedEnabledSources.map((source) => (
                                    <li key={source}>{displayTextForIssueSourceType(source)}</li>
                                ))}
                            </ul>
                        :   <Text type="secondary">No signals enabled</Text>}
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            type="link"
                            icon={<EditOutlined />}
                            disabled={isPaywalled}
                            onClick={openSourcesModal}
                        >
                            Edit
                        </PermissionedActionButton>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item
                    label={
                        <PlanRestrictedFeatureWrapper
                            feature={SubscriptionFeatureType.Triage}
                            requiresTooltip
                            shouldFakeDisabledState
                        >
                            <Text style={{ color: runwayColors.darkTextGrey }}>Ticket filters</Text>
                        </PlanRestrictedFeatureWrapper>
                    }
                >
                    <TicketFiltersSettings orgId={orgId} appId={appId} />
                </Descriptions.Item>
            </Descriptions>

            <Modal
                title="Edit signals"
                open={sourcesModalOpen}
                okText="Save"
                cancelButtonProps={{ disabled: isLoading }}
                okButtonProps={{ loading: isLoading }}
                onOk={onSaveSources}
                onCancel={() => setSourcesModalOpen(false)}
            >
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                    <Text>Select which signals Triage will use to detect issues</Text>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select signals"
                        options={sourceTypeOptions}
                        value={selectedSources}
                        onChange={setSelectedSources}
                    />
                    <Text type="secondary">Runway will not save historical data for excluded signals.</Text>
                </Space>
            </Modal>
        </div>
    );
};

export default TriageSettings;
